import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const CUENTAS_ORIGEN = [
  { value: '1.1.01.02', label: '🏦 Banco Itaú (cuenta corriente)' },
  { value: '1.1.01.01', label: '💵 Caja en pesos' },
  { value: '1.1.01.03', label: '💳 Handy a cobrar (compensación)' },
]

export default function PagosProveedores() {
  const [pendientes, setPendientes] = useState([])
  const [pagadas, setPagadas] = useState([])
  const [movimientos, setMovimientos] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null o { compra, ... }
  const [tabActiva, setTabActiva] = useState('pendientes')
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const [{ data: pend }, { data: pag }, { data: movs }] = await Promise.all([
      supabase.from('v_compras_pendientes_pago').select('*').order('fecha_emision'),
      supabase.from('compras').select('*, proveedor:proveedores(nombre)').eq('estado', 'pagada').order('fecha_pago', { ascending: false }).limit(50),
      // Movimientos bancarios sin conciliar (para vincular pago)
      supabase.from('movimientos_bancarios').select('id, fecha, concepto, debito, credito').eq('conciliado', false).gt('debito', 0).order('fecha', { ascending: false }).limit(100),
    ])
    setPendientes(pend || [])
    setPagadas(pag || [])
    setMovimientos(movs || [])
    setLoading(false)
  }

  const pendientesFil = useMemo(() => {
    const q = busqueda.toLowerCase().trim()
    if (!q) return pendientes
    return pendientes.filter(c =>
      (c.emisor_razon_social || '').toLowerCase().includes(q) ||
      (c.emisor_rut || '').includes(q) ||
      (c.proveedor_nombre || '').toLowerCase().includes(q) ||
      `${c.serie || ''}-${c.numero || ''}`.toLowerCase().includes(q)
    )
  }, [pendientes, busqueda])

  const totales = useMemo(() => {
    const t = { pendiente_total: 0, pendiente_count: 0, vencidas: 0 }
    for (const c of pendientes) {
      t.pendiente_total += Number(c.monto_total || 0)
      t.pendiente_count++
      if (c.dias_desde_emision > 30) t.vencidas++
    }
    return t
  }, [pendientes])

  async function registrarPago(form) {
    try {
      const { error } = await supabase.rpc('registrar_pago_proveedor', {
        p_compra_id: form.compra_id,
        p_fecha_pago: form.fecha_pago,
        p_cuenta_origen: form.cuenta_origen,
        p_monto: Number(form.monto) || null,
        p_movimiento_bancario_id: form.movimiento_bancario_id || null,
        p_notas: form.notas || null,
      })
      if (error) throw error
      setModal(null)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function revertir(compra) {
    if (!confirm(`¿Revertir el pago de ${compra.emisor_razon_social || 'esta compra'}?\nSe borra el asiento contable y vuelve a estado 'aprobada'.`)) return
    try {
      const { error } = await supabase.rpc('revertir_pago_proveedor', { p_compra_id: compra.id })
      if (error) throw error
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  return (
    <AdminLayout>
      <div className="pp-wrap">
        <div className="pp-headline">
          <div>
            <h1>Pagos a proveedores</h1>
            <p>Marcá cada compra como pagada → genera asiento contable automático y permite vincular el movimiento bancario.</p>
          </div>
        </div>

        {/* KPIs */}
        <div className="pp-kpis">
          <KpiCard label="Compras pendientes" value={totales.pendiente_count} color="#dc2626" sub="sin pago registrado" />
          <KpiCard label="Total a pagar" value={'$' + fmt(totales.pendiente_total)} color="#dc2626" sub="suma neta de pendientes" />
          <KpiCard label="Vencidas (+30 días)" value={totales.vencidas} color={totales.vencidas > 0 ? '#dc2626' : '#16a34a'} sub="atención prioritaria" />
        </div>

        {/* Tabs */}
        <div className="pp-tabs">
          <button className={tabActiva === 'pendientes' ? 'active' : ''} onClick={() => setTabActiva('pendientes')}>
            ⏳ Pendientes ({pendientes.length})
          </button>
          <button className={tabActiva === 'pagadas' ? 'active' : ''} onClick={() => setTabActiva('pagadas')}>
            ✓ Pagadas recientes
          </button>
        </div>

        {tabActiva === 'pendientes' && (
          <>
            <input
              type="search"
              className="pp-search"
              placeholder="Buscar por proveedor, RUT, serie-número…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
            {loading ? <div className="pp-loading">Cargando…</div> :
             pendientesFil.length === 0 ? (
              <div className="pp-empty">
                <strong>🎉 Sin compras pendientes de pago</strong>
                <p>Cuando cargues una compra (e-Factura), aparecerá acá hasta que la marques como pagada.</p>
              </div>
            ) : (
              <div className="pp-tabla-wrap">
                <table className="pp-tabla">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Proveedor</th>
                      <th>CFE</th>
                      <th style={{ textAlign: 'right' }}>Neto</th>
                      <th style={{ textAlign: 'right' }}>IVA</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th>Antigüedad</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendientesFil.map(c => {
                      const vencida = c.dias_desde_emision > 30
                      return (
                        <tr key={c.id} className={vencida ? 'pp-row-vencida' : ''}>
                          <td style={{ whiteSpace: 'nowrap' }}>{c.fecha_emision}</td>
                          <td>
                            <div className="pp-prov-nom">{c.emisor_razon_social || c.proveedor_nombre || '—'}</div>
                            <div className="pp-prov-rut">{c.emisor_rut}</div>
                          </td>
                          <td style={{ fontSize: 11, color: '#64748b' }}>{c.serie || ''}-{c.numero || ''}</td>
                          <td style={{ textAlign: 'right' }}>${fmt(c.monto_neto)}</td>
                          <td style={{ textAlign: 'right' }}>${fmt(c.iva_total)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 800 }}>${fmt(c.monto_total)}</td>
                          <td>
                            <span className={vencida ? 'pp-pill-vencida' : 'pp-pill-ok'}>
                              {c.dias_desde_emision} días
                            </span>
                          </td>
                          <td>
                            <button className="pp-btn-primary" onClick={() => setModal({
                              compra: c,
                              fecha_pago: new Date().toISOString().slice(0, 10),
                              cuenta_origen: '1.1.01.02',
                              monto: c.monto_total,
                              movimiento_bancario_id: '',
                              notas: '',
                            })}>
                              ✓ Registrar pago
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tabActiva === 'pagadas' && (
          <div className="pp-tabla-wrap">
            <table className="pp-tabla">
              <thead>
                <tr>
                  <th>Fecha emisión</th>
                  <th>Fecha pago</th>
                  <th>Proveedor</th>
                  <th>CFE</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagadas.map(c => (
                  <tr key={c.id}>
                    <td>{c.fecha_emision}</td>
                    <td style={{ fontWeight: 700, color: '#16a34a' }}>{c.fecha_pago}</td>
                    <td>{c.emisor_razon_social || c.proveedor?.nombre || '—'}</td>
                    <td style={{ fontSize: 11, color: '#64748b' }}>{c.serie}-{c.numero}</td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>${fmt(c.monto_total)}</td>
                    <td><button className="pp-btn-revert" onClick={() => revertir(c)}>Revertir</button></td>
                  </tr>
                ))}
                {pagadas.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Sin pagos registrados todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="pp-info">
          <strong>📚 Cómo funciona el flujo</strong>
          <ol>
            <li>Cargás la compra (e-Factura del proveedor) desde <code>/admin/compras</code></li>
            <li>El trigger genera un asiento automático: DEBE Egreso/COGS + DEBE IVA crédito / HABER Proveedores</li>
            <li>Acá aparece en "Pendientes" hasta que registres el pago</li>
            <li>Al registrar pago: genera asiento DEBE Proveedores / HABER Banco Itaú → cierra el HABER de Proveedores</li>
            <li>Si vinculaste un movimiento bancario, queda conciliado automático</li>
          </ol>
        </div>
      </div>

      {modal && <ModalPago modal={modal} setModal={setModal} movimientos={movimientos} onGuardar={registrarPago} />}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function ModalPago({ modal, setModal, movimientos, onGuardar }) {
  const [f, setF] = useState(modal)
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value })

  function submit(e) {
    e.preventDefault()
    if (!f.fecha_pago) { alert('Fecha pago obligatoria'); return }
    if (!f.cuenta_origen) { alert('Cuenta de origen obligatoria'); return }
    onGuardar({
      compra_id: f.compra.id,
      fecha_pago: f.fecha_pago,
      cuenta_origen: f.cuenta_origen,
      monto: f.monto,
      movimiento_bancario_id: f.movimiento_bancario_id,
      notas: f.notas,
    })
  }

  // Sugerir movimiento bancario candidato (mismo monto, fecha cercana)
  const candidatos = movimientos.filter(m =>
    Math.abs(Number(m.debito) - Number(f.compra.monto_total)) / Number(f.compra.monto_total || 1) < 0.05
  )

  return (
    <div className="pp-modal-bg" onClick={() => setModal(null)}>
      <div className="pp-modal" onClick={e => e.stopPropagation()}>
        <button className="pp-modal-close" onClick={() => setModal(null)}>×</button>
        <h2>Registrar pago</h2>
        <div className="pp-modal-info">
          <div><b>{f.compra.emisor_razon_social || 'Proveedor'}</b></div>
          <div style={{ fontSize: 12, color: '#64748b' }}>CFE {f.compra.serie}-{f.compra.numero} · Emisión {f.compra.fecha_emision}</div>
          <div className="pp-modal-monto">Total a pagar: <b>${fmt(f.compra.monto_total)}</b></div>
        </div>

        <form onSubmit={submit}>
          <div className="pp-form-row">
            <label className="pp-field">
              <span>Fecha del pago *</span>
              <input type="date" value={f.fecha_pago} onChange={upd('fecha_pago')} required />
            </label>
            <label className="pp-field">
              <span>Monto pagado *</span>
              <input type="number" step="0.01" value={f.monto} onChange={upd('monto')} required />
            </label>
          </div>

          <label className="pp-field">
            <span>Cuenta de origen del pago *</span>
            <select value={f.cuenta_origen} onChange={upd('cuenta_origen')}>
              {CUENTAS_ORIGEN.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>

          <label className="pp-field">
            <span>Movimiento bancario asociado (opcional)</span>
            <select value={f.movimiento_bancario_id} onChange={upd('movimiento_bancario_id')}>
              <option value="">— No vincular —</option>
              {candidatos.length > 0 && <optgroup label="🎯 Candidatos (mismo monto)">
                {candidatos.map(m => (
                  <option key={m.id} value={m.id}>{m.fecha} · {m.concepto} · ${fmt(m.debito)}</option>
                ))}
              </optgroup>}
              <optgroup label="Todos los movimientos sin conciliar">
                {movimientos.filter(m => !candidatos.includes(m)).map(m => (
                  <option key={m.id} value={m.id}>{m.fecha} · {m.concepto} · ${fmt(m.debito)}</option>
                ))}
              </optgroup>
            </select>
            <div className="pp-hint">Si vinculás, el movimiento queda conciliado automático contra este pago.</div>
          </label>

          <label className="pp-field">
            <span>Notas</span>
            <textarea rows={2} value={f.notas} onChange={upd('notas')} placeholder="Ej: Pago en efectivo retiro Brou Red" />
          </label>

          <div className="pp-modal-actions">
            <button type="button" className="pp-btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
            <button type="submit" className="pp-btn-primary">✓ Confirmar pago</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function KpiCard({ label, value, sub, color }) {
  return (
    <div className="pp-kpi">
      <div className="pp-kpi-l">{label}</div>
      <div className="pp-kpi-v" style={{ color }}>{value}</div>
      {sub && <div className="pp-kpi-s">{sub}</div>}
    </div>
  )
}

const CSS = `
.pp-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.pp-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.pp-headline p { font-size: 13px; color: #64748b; margin: 0 0 16px }

.pp-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 10px; margin-bottom: 18px }
.pp-kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px 14px }
.pp-kpi-l { font-size: 11px; color: #94a3b8; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.pp-kpi-v { font-size: 24px; font-weight: 900 }
.pp-kpi-s { font-size: 11px; color: #64748b }

.pp-tabs { display: flex; gap: 4px; margin-bottom: 12px; border-bottom: 2px solid #e2e8f0 }
.pp-tabs button { background: transparent; border: 0; padding: 10px 16px; font-size: 13px; font-weight: 700; color: #64748b; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -2px; font-family: inherit }
.pp-tabs button.active { color: #1A3461; border-bottom-color: #1A3461 }
.pp-tabs button:hover { color: #1A3461 }

.pp-search { width: 100%; padding: 9px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; margin-bottom: 12px; font-family: inherit }

.pp-loading, .pp-empty { padding: 40px; text-align: center; color: #94a3b8; background: #fff; border: 1px solid #e2e8f0; border-radius: 10px }
.pp-empty strong { display: block; color: #1A3461; font-size: 15px; margin-bottom: 6px }

.pp-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto }
.pp-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 900px }
.pp-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.pp-tabla td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461; vertical-align: top }
.pp-row-vencida { background: #fef2f2 }

.pp-prov-nom { font-weight: 700 }
.pp-prov-rut { font-size: 11px; color: #64748b }

.pp-pill-ok { background: #d1fae5; color: #065f46; padding: 3px 8px; border-radius: 5px; font-size: 11px; font-weight: 800 }
.pp-pill-vencida { background: #fee2e2; color: #b91c1c; padding: 3px 8px; border-radius: 5px; font-size: 11px; font-weight: 800 }

.pp-btn-primary { background: #16a34a; color: #fff; border: 0; padding: 7px 14px; border-radius: 6px; font-size: 12px; font-weight: 800; cursor: pointer; font-family: inherit; white-space: nowrap }
.pp-btn-primary:hover { background: #15803d }
.pp-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 9px 16px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.pp-btn-revert { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 4px 10px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer; font-family: inherit }
.pp-btn-revert:hover { background: #fee2e2 }

.pp-info { margin-top: 22px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.pp-info strong { font-size: 13px }
.pp-info ol { margin: 8px 0; padding-left: 22px }
.pp-info code { background: #dbeafe; padding: 1px 5px; border-radius: 3px; font-size: 11.5px }

/* Modal */
.pp-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.pp-modal { background: #fff; border-radius: 14px; max-width: 580px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative }
.pp-modal h2 { margin: 0 0 14px; font-size: 20px; color: #1A3461; font-weight: 900 }
.pp-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }
.pp-modal-info { padding: 12px 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 14px; font-size: 13px }
.pp-modal-monto { margin-top: 6px; font-size: 14px; color: #1A3461 }
.pp-modal-monto b { font-size: 18px; color: #dc2626 }
.pp-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 16px; padding-top: 14px; border-top: 1px solid #e2e8f0 }

.pp-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px }
@media (max-width: 540px) { .pp-form-row { grid-template-columns: 1fr } }
.pp-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.pp-field span { font-size: 12px; font-weight: 700; color: #475569 }
.pp-field input, .pp-field select, .pp-field textarea { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1A3461 }
.pp-hint { font-size: 11px; color: #64748b; margin-top: 2px }
`
